import { API_BASE_URL } from "../apiConfig";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Signup.css";

const Signup = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);

  const handleSignup = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    // Validation
    if (!username || !email || !password || !confirmPassword || !role) {
      setError("All fields are required");
      setLoading(false);
      return;
    }

    // Username validation
    if (!/^[A-Z]/.test(username)) {
      setError("Username must start with a capital letter");
      setLoading(false);
      return;
    }
    if (!/^[A-Za-z0-9!@#$%^&*]+$/.test(username)) {
      setError("Username may only include letters, numbers, and these special characters: !@#$%^&*");
      setLoading(false);
      return;
    }
    const specialChars = (username.match(/[!@#$%^&*]/g) || []).length;
    if (specialChars > 2) {
      setError("Username may include at most 2 special characters");
      setLoading(false);
      return;
    }

    if (role === "recycling_company" && !companyName.trim()) {
      setError("Company name is required");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    // Email format and domain validation
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    const allowedDomains = ["gmail.com", "yahoo.com", "outlook.com"];
    const emailDomain = email.split("@")[1];
    if (!allowedDomains.includes(emailDomain)) {
      setError("Only @gmail, @yahoo, and @outlook are accepted");
      setLoading(false);
      return;
    }

    // Password validation for User and Driver
    if (role === "user" || role === "driver") {
      if (password.length < 8) {
        setError("Password must be at least 8 characters long");
        setLoading(false);
        return;
      }
      if (!/^[A-Z]/.test(password)) {
        setError("First letter must be capital");
        setLoading(false);
        return;
      }
      if (!/[a-z]/.test(password)) {
        setError("Password must contain at least one small letter");
        setLoading(false);
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        setError("Password must contain at least one special character");
        setLoading(false);
        return;
      }
    }

    // 🔹 Step 1: Send OTP if not sent yet
    if (!showOtpInput) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });

        const data = await res.json();
        if (!res.ok) {
          setError(data.message || "Failed to send OTP");
          setLoading(false);
          return;
        }

        setSuccess("Verification code sent to your email!");
        setShowOtpInput(true);
        setLoading(false);
        return;
      } catch (err) {
        setError("Failed to connect to server");
        setLoading(false);
        return;
      }
    }

    // 🔹 Step 2: Verify OTP and Signup
    if (!otp || otp.length !== 4) {
      setError("Please enter the 4-digit verification code");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password, role, otp, companyName }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Server error occurred");
        setLoading(false);
        return;
      }

      setSuccess("Creating account...");
      setTimeout(() => {
        setSuccess("Registered Successfully!");
        setLoading(false);

        // Clear form
        setUsername("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setRole("");
        setCompanyName("");
        setOtp("");
        setShowOtpInput(false);
      }, 3000);

    } catch (err) {
      setError("Server error");
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-box">
        <h3 className="title">Sign Up</h3>

        {/* Username */}
        <div className="input-wrapper">
          <label className="input-label">Username</label>
          <input
            className="input-field"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username"
          />
          <span className="input-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="7" r="4" />
              <path d="M5.5 21c0-4.5 7-4.5 7-4.5s7 0 7 4.5" />
            </svg>
          </span>
        </div>

        {/* Email */}
        <div className="input-wrapper">
          <label className="input-label">Email</label>
          <input
            className="input-field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email"
          />
          <span className="input-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 6l-10 7L2 6" />
            </svg>
          </span>
        </div>

        {/* Password */}
        <div className="input-wrapper">
          <label className="input-label">Password</label>
          <input
            type={showPassword ? "text" : "password"}
            className="input-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
          />
          <span className="input-icon" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            )}
          </span>
        </div>

        {/* Confirm Password */}
        <div className="input-wrapper">
          <label className="input-label">Confirm Password</label>
          <input
            type={showConfirmPassword ? "text" : "password"}
            className="input-field"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm password"
          />
          <span className="input-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
            {showConfirmPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            )}
          </span>
        </div>

        {/* Roles */}
        <div className="roles">
          <div className={`role-box ${role === "user" ? "active" : ""}`} onClick={() => setRole("user")}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="7" r="4" />
              <path d="M5.5 21c0-4.5 7-4.5 7-4.5s7 0 7 4.5" />
            </svg>
            <p>User</p>
          </div>
          <div className={`role-box ${role === "driver" ? "active" : ""}`} onClick={() => setRole("driver")}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
            <p>Driver</p>
          </div>
          <div className={`role-box ${role === "recycling_company" ? "active" : ""}`} onClick={() => setRole("recycling_company")}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <polyline points="23 20 23 14 17 14" />
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
            <p>Recycling</p>
          </div>
        </div>

        {/* Company Name — only for recycling_company */}
        {role === "recycling_company" && (
          <div className="input-wrapper">
            <label className="input-label">Company Name</label>
            <input
              className="input-field"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
            />
            <span className="input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
              </svg>
            </span>
          </div>
        )}

        {/* OTP Input (Conditionally visible) */}
        {showOtpInput && (
          <div className="input-wrapper otp-wrapper">
            <label className="input-label">Verification Code (4 Digits)</label>
            <input
              className="input-field"
              value={otp}
              maxLength="4"
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="Enter 4-digit code"
            />
            <span className="input-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
          </div>
        )}

        {error && <p className="error-text">{error}</p>}

        {/* Signup Button */}
        <button className="signup-btn" onClick={handleSignup} disabled={loading}>
          {showOtpInput ? "Verify & Signup" : "Signup"}
        </button>

        {/* Messages */}
        {success && <p className={loading ? "info-text" : "success-text"}>{success}</p>}

        <p className="login-text">
          Already have an account?{" "}
          <span className="login-link" onClick={() => navigate("/login")}>
            Login
          </span>
        </p>
      </div>
    </div>
  );
};

export default Signup;
